import { useEffect, useRef, useState, memo, useCallback } from "react";
import { ReactComponent as PlayIcon } from "components/icons/play_icon.svg";

import "./VideoComponent.scss";
import { className } from "../../utils";

const FULL = 100;

export const VideoComponent = (props: {
  video: string;
  click: () => void;
  sound: boolean;
  seek?: number;
  poster: string;
  canPlay: boolean;
  fullscreen: boolean;
  onProgress: (buffered: number, position: number) => void;
  onPlay: () => void;
}) => {
  const { onProgress, onPlay } = props;

  const ref = useRef<HTMLVideoElement>(null);

  const [paused, setPaused] = useState(false);
  const [error, setError] = useState<string|null>(null);
  const [isTriedToPlay, setIsTriedToPlay] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Stop playback on tab change.
  useEffect(() => {
    const handler = () => {
      if (document.hidden) {
        if (ref.current) {
          ref.current.pause();
          setPaused(true);
        }
      }
    };

    document.addEventListener("visibilitychange", handler);

    return () => {
      document.removeEventListener("visibilitychange", handler);
    };
  }, []);

  const play = useCallback(() => {
    if (!ref.current) {
      return;
    }

    // On some iOS devices, play() throws an exception:
    //
    // NotAllowedError: The request is not allowed by the user agent or the platform
    // in the current context, possibly because the user denied permission.
    //
    // See: https://trello.com/c/uBxK9w2r
    try {
      const playPromise = ref.current.play();

      if (playPromise !== undefined) {
        playPromise.then(() => {
          setPaused(false);
          onPlay();
        }).catch((e) => {
          console.warn("failed to play video", e);
          setPaused(true);
          setIsTriedToPlay(true);
        }).finally(() => {
          setIsTriedToPlay(true);
        });
      } else {
        setPaused(false);
        setIsTriedToPlay(true);
        onPlay();
      }
    } catch (e) {
      console.warn("failed to play video", e);
      setPaused(true);
      setIsTriedToPlay(true);
    }
  }, [onPlay]);

  const pause = useCallback(() => {
    if (!ref.current) {
      return;
    }

    ref.current.pause();
    setPaused(true);
  }, []);

  // Seek to the right position but only when it changes.
  useEffect(() => {
    const em = ref.current;

    if (props.seek && em) {
      const time: number = em.duration * props.seek / FULL;
      if (Number.isFinite(time)) {
        em.currentTime = time;
      }
    }
  }, [props.seek, ref]);

  useEffect(() => {
    if (!ref.current || error) {
      return;
    }

    if (props.canPlay && ref.current.paused) {
      play();
      return;
    }

    if (!props.canPlay && !ref.current.paused) {
      pause();
    }
  }, [props.canPlay, play, pause, error, props.video]);

  const handleClick = useCallback(() => {
    if (!ref.current || error) {
      return;
    }

    ref.current.paused ? play() : pause();
  }, [error, play, pause]);

  const handleProgress = useCallback(() => {
    const em = ref.current;

    if (em) {
      if (em.buffered.length > 0) {
        const buf = em.buffered.end(0);
        const buf_pc = buf * FULL / em.duration;
        const cur_pc = em.currentTime * FULL / em.duration;

        onProgress(buf_pc, cur_pc);
      }
    }
  }, [onProgress]);

  const handleError = useCallback(() => {
    const v = ref.current;

    if (v) {
      if (v.networkState === v.NETWORK_NO_SOURCE) {
        setError("Error downloading the video file.");
      } else {
        setError("Unknown error.");
      }
    } else {
      console.error("Video playback failed, but the ref is not yet ready.");
    }
  }, []);

  const handleLoadedData = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const preload = props.fullscreen ? "none" : "auto";

  return (
    <div className="videoWrapper">
      {
        !isLoaded && (
          <img src={props.poster} alt=""/>
        )
      }

      <video
        className={className({
          error: !!error,
          isLoaded: isLoaded,
        })}
        controls={false}
        autoPlay={false}
        loop={true}
        playsInline
        preload={preload}
        onClick={handleClick}
        onProgress={handleProgress}
        onDurationChange={handleProgress}
        onTimeUpdate={handleProgress}
        onError={handleError}
        onLoadedData={handleLoadedData}
        muted={!props.sound}
        ref={ref}
        disablePictureInPicture={true}
        src={props.video}
      />

      {paused && isTriedToPlay && !error && (
        <PlayIcon
          className="playButton"
          onClick={handleClick}
        />
      )}

      {error && (
        <p className="error">{error}</p>
      )}
    </div>
  );
};

export const VideoComponentMemo = memo(VideoComponent);
